import { Dialog, Menu } from '@base-ui/react';
import cx from 'classnames';
import { secondsToMilliseconds } from 'date-fns';
import { debounce } from 'lodash-es';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, ButtonVariants } from '@components/button/Button';
import { Icon } from '@components/icon/Icon';
import { errorToast, successToast } from '@components/toasts/Toasts';
import { ApiError } from '@shared/api/errors';
import { useIsOpen } from '@shared/hooks/useIsOpen';
import { reportAppError } from '@shared/reportAppError';
import { appendGuestCountLabel } from '@shared/utils/guestCount';
import { ContactInfo } from 'restaurantAdmin/components/contactInfo/ContactInfo';
import { RESERVATIONS_WAITLIST_PAGE_PATH } from 'restaurantAdmin/paths';
import { WAITLIST_ENTRY_INDEX_PARAM } from 'restaurantAdmin/utils/constants';
import { useWaitListContext } from 'restaurantAdmin/reservations/service/sidePanel/waitList/state/WaitListContext';
import typography from '~styles/typography.scss';
import { useServiceSearchParams } from '../../hooks/useServiceSearchParams';
import optionsMenuStyles from '../../OptionsMenu.scss';
import { sendTableReadyMessage } from './apiHelpers';
import { RemoveFromWaitListConfirmationModal } from './RemoveFromWaitListConfirmationModal';
import styles from './ViewWaitlistEntryDrawer.scss';
import { WaitListTimer } from './WaitListTimer';
import { useReservationServiceSelectedSeatingContext } from '../../state/ReservationServiceSelectedSeatingContext';

const SEND_TABLE_READY_DEBOUNCE_DURATION = secondsToMilliseconds(1);

const formatWaitTimeDuration = (waitTime: number) => {
  const hours = Math.floor(waitTime / 60);
  const minutes = waitTime % 60;

  return `${hours}hr ${minutes}min`;
};

export const ViewWaitlistEntryDrawer = () => {
  const navigate = useNavigate();
  const { openWaitListSeatDrawer, searchParams } = useServiceSearchParams();

  const {
    close: closeRemoveFromWaitlistModal,
    isOpen: removeFromWaitListModalIsOpen,
    open: openRemoveFromWaitlistModal,
  } = useIsOpen();

  const {
    selectedWaitListEntry,
    fetchWaitListEntries,
    setSelectedWaitListEntryIndex,
  } = useWaitListContext();

  const { clearSelectedFloorPlanTables } =
    useReservationServiceSelectedSeatingContext();

  useEffect(() => {
    setSelectedWaitListEntryIndex(
      Number.parseInt(searchParams.get(WAITLIST_ENTRY_INDEX_PARAM) || '0', 10),
    );
  }, []);

  const handleClickSendTableReadyMessage = useMemo(
    () =>
      debounce(async () => {
        try {
          if (!selectedWaitListEntry) return;
          await sendTableReadyMessage(
            selectedWaitListEntry.restaurantId,
            selectedWaitListEntry.id,
          );
        } catch (e) {
          if (e instanceof ApiError) {
            errorToast({ message: e.message });
          }
          reportAppError(e);
          return;
        }
        fetchWaitListEntries();
        successToast({ message: 'SMS message sent' });
      }, SEND_TABLE_READY_DEBOUNCE_DURATION),
    [selectedWaitListEntry],
  );

  if (!selectedWaitListEntry) return null;

  const hasMoreThanOneSendTableReadyMessageEvent =
    selectedWaitListEntry.events.filter(
      (event) => event.type === 'sendTableReadyMessage',
    ).length > 1;

  const handleCancellationOfWaitlistRemovalModal = () => {
    fetchWaitListEntries();
    navigate(RESERVATIONS_WAITLIST_PAGE_PATH);
  };

  const handleSeatGuest = () => {
    clearSelectedFloorPlanTables();
    openWaitListSeatDrawer();
    closeRemoveFromWaitlistModal();
  };

  return (
    <div className={styles.container} aria-label="Wait List Form">
      <div className={styles.headerBar}>
        <div className={styles.entryDetails}>
          <div className={styles.entryDetailsRow}>
            <p className={cx(typography.h5, styles.guestName)}>
              {selectedWaitListEntry.firstName} {selectedWaitListEntry.lastName}
            </p>

            <WaitListTimer waitListEntry={selectedWaitListEntry} />
          </div>

          <div className={styles.entryDetailsRow}>
            <p className={cx(typography.t1, styles.guestCount)}>
              {appendGuestCountLabel(selectedWaitListEntry.guestCount)}
            </p>

            <p className={cx(typography.t1, styles.waitTime)}>
              {formatWaitTimeDuration(selectedWaitListEntry.estimatedWait)} wait
              estimate
            </p>
          </div>
        </div>

        <div className={styles.headerButtons}>
          <Dialog.Close
            render={({ onClick }) => (
              <Button
                label="Close"
                onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
                variant={ButtonVariants.Tertiary}
              />
            )}
          />

          <Button
            isDisabled={hasMoreThanOneSendTableReadyMessageEvent}
            label="Table is Ready"
            variant={ButtonVariants.Secondary}
            onClick={handleClickSendTableReadyMessage}
          />

          <Button
            label="Seat Guest"
            variant={ButtonVariants.Primary}
            onClick={handleSeatGuest}
          />

          <Menu.Root>
            <Menu.Trigger
              aria-label="Waitlist options menu"
              className={cx(typography.h8, optionsMenuStyles.button)}
            >
              <Icon name="options" className={optionsMenuStyles.icon} />
            </Menu.Trigger>

            <Menu.Portal>
              <Menu.Positioner
                side="bottom"
                sideOffset={12}
                className={optionsMenuStyles.positioner}
              >
                <Menu.Popup className={optionsMenuStyles.popup}>
                  <Menu.Arrow>
                    <Icon name="arrowPopover" />
                  </Menu.Arrow>

                  <Menu.Item
                    className={optionsMenuStyles.menuItem}
                    onClick={openRemoveFromWaitlistModal}
                  >
                    Remove from Waitlist
                  </Menu.Item>
                </Menu.Popup>
              </Menu.Positioner>
            </Menu.Portal>
          </Menu.Root>
        </div>
      </div>

      <div className={styles.waitlistDetails}>
        <ContactInfo phone={selectedWaitListEntry.phone} />
      </div>

      <RemoveFromWaitListConfirmationModal
        closeModal={closeRemoveFromWaitlistModal}
        isOpen={removeFromWaitListModalIsOpen}
        onCanceled={handleCancellationOfWaitlistRemovalModal}
        waitListEntry={selectedWaitListEntry}
      />
    </div>
  );
};
